/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { CSSProperties, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { ResponsivePie } from '@nivo/pie';

import { CIColors, CIFonts, ClusterStates } from 'shared';

import { ChartTooltip } from '../../../../components/ChartTooltip/ChartTooltip';
import {
  type BaseChartData,
  type ChartDimensions,
  defaultChartDimensions,
  defaultChartMargin,
  defaultChartTheme,
  defaultPieConfig,
} from '../../../../constants/chartConfig';
import { Namespaces } from '../../../../i18n';
import { HomePageChartLoader } from '../../../../skeletons/HomePageChartLoader';
import { ProductStatus } from '../../../../types/product/ProductStatus';
import { determineStatusColor } from '../../../../utils/determineStatusColor';
import { createChartStyles } from './Chart.styles';

type ChartProps = {
  productsStatistic: ProductStatus;
  isLoading: boolean;
  hideEmptyStatuses?: boolean;
  style: CSSProperties;
  dimensions?: ChartDimensions;
};

export const Chart = memo<ChartProps>(props => {
  const classes = createChartStyles();
  const { t } = useTranslation(Namespaces.Common);
  const { productsStatistic, isLoading, hideEmptyStatuses, dimensions = defaultChartDimensions } = props;
  const isEmptyData = !Object.values(productsStatistic).some(el => el > 0);

  const data = useMemo((): BaseChartData[] => {
    let tmpChartData: BaseChartData[] = [];
    const keys = Object.keys(productsStatistic);
    const values = Object.values(productsStatistic);

    keys.forEach((key, index) => {
      tmpChartData.push({
        value: values[index],
        id: key,
        label: key,
        color: determineStatusColor(key as ClusterStates),
      });
    });

    if (hideEmptyStatuses) {
      tmpChartData = tmpChartData.filter(i => i.value > 0);
    }

    return tmpChartData;
  }, [hideEmptyStatuses, productsStatistic]);

  return (
    <>
      {isLoading && (
        <div className={classes.skeletonOffset}>
          <HomePageChartLoader />
        </div>
      )}
      {!isLoading && isEmptyData && (
        <Box
          component="div"
          sx={{
            border: '1px solid #EEEEEE',
            margin: 0,
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            height: '300px',
          }}
        >
          <Typography mt={3} ml={3} variant="h4" component="h4" gutterBottom>
            {t('home.services_status')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              overflowY: 'auto',
            }}
          >
            <Typography ml={3} fontSize={'13px'}>
              {t('home.no_chart_data')}
            </Typography>
          </Box>
        </Box>
      )}
      {!isLoading && !isEmptyData && (
        <Box component="div" className={classes.root}>
          <Typography variant="h4" component="h4" style={{ marginBottom: 16 }}>
            {t('home.services_status')}
          </Typography>
          <div
            // className={classes.root}
            style={{
              margin: 0,
              padding: 0,
              display: 'flex',
            }}
          >
            <div
              style={{
                width: dimensions.width,
                height: dimensions.height,
              }}
            >
              <ResponsivePie
                data={data}
                margin={defaultChartMargin}
                {...defaultPieConfig}
                colors={d => d.data.color}
                tooltip={({ datum }) => (
                  <ChartTooltip label={String(datum.label)} value={String(datum.value)} style={{ minWidth: 160 }} />
                )}
                theme={defaultChartTheme}
              />
            </div>
            <div
              style={{
                marginLeft: 32,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minWidth: 120,
                minHeight: 225,
              }}
            >
              {data.map(item => (
                <div key={item.id} style={{ marginBottom: 8, display: 'flex', alignItems: 'baseline' }}>
                  <span
                    style={{
                      color: item.color,
                      fontWeight: 700,
                      fontSize: 28,
                      marginRight: 8,
                      fontFamily: CIFonts.Text,
                      lineHeight: 1,
                    }}
                  >
                    {item.value}
                  </span>
                  <span
                    style={{
                      color: CIColors.Text,
                      fontWeight: 400,
                      fontSize: 18,
                      fontFamily: CIFonts.Text,
                      marginRight: 8,
                    }}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Box>
      )}
    </>
  );
});
